/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2020, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import { Injectable, TemplateRef } from '@angular/core';
import { Logger } from '@core/lib/services';
import { environment } from '@env/environment.prod';
import { NotSupportedException } from '@core/lib/exceptions';
import { defer } from 'rxjs';
import { catchError, delay, retryWhen, scan } from 'rxjs/operators';
import { appConstants } from '@app/common/app-constants';
import { DiQueue } from '@app/di/data-model/di-queue';
import { WindowRef } from '@app/common/window-ref.service';
import { StudyDataParam } from '@app/common/services/bring-online/bring-online.types';
import { SearchCriteriaType, SearchParams } from '@app/common/types/search-params';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ExamSharingPageService } from '@app/di/exam-sharing/exam-sharing-page.service';
import { v4 as uuidV4 } from 'uuid';
import { FhirCastResource } from '@app/di/channel/fhircast/model/fhircast-resource';
import { ImagingStudy, Patient } from 'fhir/r4';

const log = new Logger('UtilService');

@Injectable({
  providedIn: 'root'
})
export class UtilService {
  static maximumTabsAllowed = 5;
  private static readonly whitespaceTester = /^\s*$/;
  private readonly applicationLaunchUrlParameters: string = null;
  private viewerMessageQueue = new DiQueue();
  private uvEnabled = false;
  private associationFlag;
  private prefixRegex = /[%|*]$/;
  private suffixRegex = /^[%|*]/;
  private isDifferentPatientFlag = false;
  private appBaseUrl;
  private specialCharArr = ['ö', 'ä', 'ü', 'ß'];
  private specialCharUpperCaseArr = ['Ü', 'Ö', 'Ä', 'ẞ'];
  private specialCharVariantArr = ['oe', 'ae', 'ue', 'ss'];
  private specialCharUpperCaseVariantArr = ['UE', 'OE', 'AE', 'SS'];
  private umlautRegEx = {
    ue: 'ü',
    oe: 'ö',
    ae: 'ä',
    ss: 'ß'
  };
  private umlautUpperCaseRegEx = {
    UE: 'Ü',
    OE: 'Ö',
    AE: 'Ä',
    SS: 'ẞ'
  };
  private umlautVarientRegEx = {
    ü: 'ue',
    ö: 'oe',
    ä: 'ae',
    ß: 'ss'
  };
  private umlautVarientUpperCaseRegEx = {
    Ü: 'UE',
    Ö: 'OE',
    Ä: 'AE',
    ẞ: 'SS'
  };
  private _filterCriteriaLimitTemplate: TemplateRef<any>;
  private externalLoginApplication = undefined;
  private systemProp;
  refresh = false;

  proxyPath;
  sharedTabContext: any[] = [];
  static lights = false;

  static parseInt(str, defaultValue?: number) {
    let res = parseInt(str, 10);
    if (isNaN(res)) {
      res = defaultValue === undefined ? str : defaultValue;
    }
    return res;
  }

  static findIndexById(id, list: Array<any>) {
    for (let index = 0; index < list.length; index++) {
      if (list[index].requestedProcId === id) {
        return index;
      }
    }
    return -1;
  }

  static isEmpty(str) {
    return str === null || str === undefined || str === '';
  }

  static isNull(val) {
    return val === 'null' || val === null || val === '' || typeof val === 'undefined' || val === 'undefined';
  }

  static isDefined(value) {
    return typeof value !== 'undefined';
  }

  static convertToBool(value) {
    return parseInt(value, 10) > 0;
  }

  static formatToShortDate(date: Date) {
    return [date.getMonth() + 1, date.getDate(), date.getFullYear()].join('/');
  }

  static convertValueToType(value, toType?) {
    if (!toType) {
      return value;
    } else if (toType === 'string') {
      return value.toString();
    } else if (toType === 'int') {
      return parseInt(value, 10);
    }

    throw new NotSupportedException('Unsupported conversion to "' + toType + '" specified for mapping.');
  }

  static isContentfulString(obj) {
    return _.isString(obj) && !_.isEmpty(obj) && !UtilService.whitespaceTester.test(obj);
  }

  static isEnabled(value): boolean {
    if (value === 'true') {
      return true;
    } else if (value === 'false') {
      return false;
    }
  }

  static isNonAddendumReport(responseInfo): boolean {
    return !!(
      appConstants.AddendumStatus.No === responseInfo.addendumStatus &&
      (appConstants.ReportStatus.Draft === responseInfo.reportStatus ||
        appConstants.ReportStatus.WetRead === responseInfo.reportStatus ||
        appConstants.ReportStatus.Corrected === responseInfo.reportStatus ||
        appConstants.ReportStatus.Final === responseInfo.reportStatus ||
        appConstants.ReportStatus.PendingCorrection === responseInfo.reportStatus ||
        appConstants.ReportStatus.PendingSignature === responseInfo.reportStatus)
    );
  }

  /**
   * Convert ReportStatus value from Reporting Application
   */
  static convertReportStatus(response) {
    const reportStatus = response.operationStatus.Status;
    if (!_.isUndefined(reportStatus) && !_.isEmpty(reportStatus)) {
      if (!_.isUndefined(appConstants.ReportStatus[reportStatus])) {
        response.operationStatus.Status = appConstants.ReportStatus[reportStatus];
        response.reportStatus = appConstants.ReportStatus[reportStatus];
      }
    }
  }

  /**
   * Convert Addendum value from Reporting Application
   */
  static convertAddendum(response) {
    const addendum = response.operationStatus.Addendum;
    if (addendum === 'Y') {
      response.operationStatus.Addendum = appConstants.AddendumStatus.Yes;
      response.addendumStatus = appConstants.AddendumStatus.Yes;
    } else if (addendum === 'N' || UtilService.isEmpty(addendum)) {
      response.operationStatus.Addendum = appConstants.AddendumStatus.No;
      response.addendumStatus = appConstants.AddendumStatus.No;
    }
  }

  static isNonFinalAddendumReport(responseInfo): boolean {
    return !!(
      appConstants.AddendumStatus.Yes === responseInfo.addendumStatus &&
      (appConstants.ReportStatus.Draft === responseInfo.reportStatus ||
        appConstants.ReportStatus.Corrected === responseInfo.reportStatus ||
        appConstants.ReportStatus.PendingCorrection === responseInfo.reportStatus ||
        appConstants.ReportStatus.PendingSignature === responseInfo.reportStatus) &&
      !responseInfo.isDiscardXML
    );
  }

  static getExamStatusToUpdate(addendumStatus, reportStatusPS) {
    let reportStatus;
    if (addendumStatus === '-1' || addendumStatus === '1') {
      reportStatus = appConstants.WFMAddendumReportStatuses[reportStatusPS];
    } else {
      reportStatus = appConstants.WFMReportStatuses[reportStatusPS];
    }
    log.info('getExamStatusToUpdate - ReportStatus -' + reportStatus);
    return reportStatus;
  }

  static isZeroImageExam(exam): boolean {
    let imageCount = exam.imageCount;
    imageCount = parseInt(imageCount, 10);
    imageCount = imageCount ? imageCount : 0;
    return imageCount === 0;
  }

  static isRemoteExam(object): boolean {
    if (object && object.keys) {
      return (
        !!object.keys.repositoryType && object.keys.repositoryType !== 'Local' && object.keys.repositoryType !== 'GWL'
      );
    } else {
      return (
        !!object && !!object.repositoryType && object.repositoryType !== 'Local' && object.repositoryType !== 'GWL'
      );
    }
  }

  static isGlobalExam(object): boolean {
    if (object && object.keys) {
      return !!object.keys.repositoryType && object.keys.repositoryType === 'GWL';
    } else {
      return !!object && !!object.repositoryType && object.repositoryType === 'GWL';
    }
  }

  static isExamOnline(studyDataAvailability) {
    return (
      studyDataAvailability === appConstants.studyDataAvailability.ONLINE ||
      studyDataAvailability === appConstants.studyDataAvailability.NEARLINE
    );
  }

  static isArchivedExam(archiveAdopted) {
    return (
      !_.isUndefined(archiveAdopted) &&
      !_.isNull(archiveAdopted) &&
      (archiveAdopted === true || archiveAdopted === 'YES')
    );
  }

  static disableBringOnline(archiveFetchStatus, workItem) {
    return archiveFetchStatus === appConstants.studyDataAvailability.THIRD_PARTY && workItem.imageCount === 0;
  }

  static fetchStudyDataParam(exam: any): StudyDataParam {
    return {
      archiveAdoptedStatus: exam?.archiveAdoptedStatus,
      studyDataAvailability: exam?.studyDataAvailability,
      imageCount: exam?.imageCount
    };
  }

  static isThirdParty(studyDataAvailability: string) {
    return (
      !_.isUndefined(studyDataAvailability) &&
      !_.isNull(studyDataAvailability) &&
      studyDataAvailability === appConstants.studyDataAvailability.THIRD_PARTY
    );
  }

  static isValidAssociateExam(
    statusId,
    isReferenceOnlyToDictated,
    isOrderedAllowedForAssociation,
    isScheduledAllowedForAssociation
  ): boolean {
    return (
      statusId === appConstants.ExamStatus.ARRIVED ||
      statusId === appConstants.ExamStatus.VERIFIED ||
      (statusId === appConstants.ExamStatus.ORDERED && isOrderedAllowedForAssociation) ||
      (statusId === appConstants.ExamStatus.SCHEDULED && isScheduledAllowedForAssociation) ||
      (statusId === appConstants.ExamStatus.REFERENCE_ONLY && isReferenceOnlyToDictated)
    );
  }

  static isSearchList(list: any): boolean {
    return !!list && !!list.searchCriteriaInfo;
  }

  static fromJson(json) {
    return UtilService.isString(json) ? JSON.parse(json) : json;
  }

  static isString(value): boolean {
    return typeof value === 'string';
  }

  static getUniqueIdentifier(): string {
    const date = new Date();
    const sec = date.getSeconds();
    const milSec = date.getMilliseconds();
    const contextTokenMax = 10000;
    const contextTokenMin = 1;

    // Random number generated between 1 and 10000.
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    const randomNumber = (array[0] % (contextTokenMax - contextTokenMin + 1)) + contextTokenMin;
    // Append time in second and millisecond to the random number.
    // Converted to string so that they can concatenate.
    return randomNumber.toString() + sec.toString() + milSec.toString();
  }

  static isBrowserSupported() {
    const ieVersion = UtilService.getIEVersion();
    log.debug('Browser Detail :' + window.navigator.userAgent + '>> IE Version: ' + ieVersion);
    if (!!window.WebSocket && UtilService.isOtherNonIEBrowsersSupported()) {
      return true;
    }
    return false;
  }

  static checkAssociatedCloseAll(closedTabIds, targetId) {
    if (closedTabIds && closedTabIds.length > 0) {
      if (!_.contains(closedTabIds, targetId)) {
        return true;
      }
    } else {
      return true;
    }
  }

  static getId(id): number | any {
    let targetId = parseInt(id);
    targetId = _.isNaN(targetId) ? id : targetId;
    return targetId;
  }

  static isOpenedExternally(workItem) {
    const state = workItem && (workItem.state || workItem.keys?.state);
    if (state && state.ownership) {
      const assignTo = state.ownership.assignedToUser;
      if (assignTo && assignTo.length > 0) {
        if (
          state.extAppWorkItemState === 'External_Read_Lock_Added' &&
          !UtilService.isActionOverrideAllowed(workItem)
        ) {
          return true;
        }
      }
    }
    return false;
  }

  static isOpenedForRMSEdit(workItem) {
    const state = workItem && (workItem.state || workItem.keys?.state);
    if (state && state.activity) {
      if (state.activity?.editing !== null) {
        return true;
      }
    }
    return false;
  }
  static isActionOverrideAllowed(workItem) {
    const state = workItem && (workItem.state || workItem.keys?.state);
    if (state) {
      return (
        state.ownership?.assignedToUser[0]['extStatusFlag'] === 1 ||
        state.ownership?.assignedToUser[0]['extStatusFlag'] === 9
      );
    }
    return false;
  }

  static isAssignedToSelf(workItem, currentUser) {
    const state = workItem && (workItem.state || workItem.keys?.state);
    if (state) {
      const isAssigned =
        state.ownership &&
        state.ownership.assignedToUser &&
        state.ownership.assignedByUser &&
        state.ownership.assignedOn;

      return isAssigned && state.ownership.assignedToUser[0].userName === currentUser;
    }
    return false;
  }

  static isBeingRead(workItem) {
    const state = workItem && (workItem.state || workItem.keys?.state);
    return !!(state && state.activity && state.activity.reading);
  }

  static isActivatedBySelf(workItem, currentUser) {
    const state = workItem && (workItem.state || workItem.keys?.state);
    if (state && state.activity) {
      const activity = state.activity;
      const isActivated = activity && activity.readingDto;

      return isActivated && activity.readingDto.userName === currentUser;
    }
    return false;
  }

  static isActivatedByOther(workItem, currentUser) {
    const state = workItem && (workItem.state || workItem.keys?.state);
    const readingUserName = state?.activity?.readingDto?.userName;

    return !!readingUserName && readingUserName !== currentUser;
  }

  static isAssignedToOther(workItem, currentUser) {
    const state = workItem && (workItem.state || workItem.keys?.state);
    if (state && state.ownership) {
      const assignTo = state.ownership.assignedToUser;
      if (assignTo && assignTo.length > 0) {
        if (UtilService.isActionOverrideAllowed(workItem) && currentUser !== assignTo[0].userName) {
          return true;
        }
      }
    }
    return false;
  }

  static checkAssignedAndActivatedBySelf(workItem, userId) {
    return UtilService.isAssignedToSelf(workItem, userId) && UtilService.isActivatedBySelf(workItem, userId);
  }

  static checkAssignedToSelfAndActionOverrideAllowed(workItem, userId) {
    return UtilService.isAssignedToSelf(workItem, userId) && UtilService.isActionOverrideAllowed(workItem);
  }

  static checkAssignedToOtherOrOpenedExternally(workItem, userId) {
    return (
      UtilService.isAssignedToOther(workItem, userId) ||
      UtilService.isOpenedExternally(workItem) ||
      UtilService.isOpenedForRMSEdit(workItem)
    );
  }

  static collectIdsToClearTabWorlist(readCloseWorkItems, ids, diagnosticHubCloseWorkItems) {
    if (readCloseWorkItems.length > 0) {
      _.forEach(readCloseWorkItems, exam => {
        ids.push(exam.workItem.id + '');
      });
    }
    if (diagnosticHubCloseWorkItems.length > 0) {
      _.forEach(diagnosticHubCloseWorkItems, exam => {
        ids.push(exam.id + '');
      });
    }
  }

  static parseFilter(filter): string {
    const wrappedFilter = _({});
    return UtilService.joinParams(
      wrappedFilter.extend[filter instanceof Array ? 'apply' : 'call'](wrappedFilter, filter)
    );
  }

  static joinParams(data): string {
    const params = [];
    let val;
    for (const prop in data) {
      if (data.hasOwnProperty(prop)) {
        if (Array.isArray(data[prop]) && data[prop].length > 0) {
          val = data[prop].join(',');
        } else if (typeof data[prop] === 'string' || typeof data[prop] === 'number') {
          val = data[prop];
        }

        if (val || val === 0) {
          params.push([prop, val].join(':'));
        }
      }
    }
    return params.join(';');
  }

  checkIfSpecialCharPresent(name: string): boolean {
    const nameLowerCase = name.toLowerCase();
    let isPresent: boolean;
    this.specialCharArr.some(value => {
      if (nameLowerCase.includes(value)) {
        isPresent = true;
        return isPresent;
      }
    });
    return isPresent;
  }

  checkIfSpecialCharVariantPresent(name: string): boolean {
    const nameLowerCase = name.toLowerCase();
    let isPresent: boolean;
    this.specialCharVariantArr.some(value => {
      if (nameLowerCase.includes(value)) {
        isPresent = true;
        return isPresent;
      }
    });
    return isPresent;
  }

  replaceSpecialChar(input: string): string {
    // replace all lower Umlauts
    let output = input;
    this.specialCharVariantArr.forEach(value => {
      const regEx = new RegExp(this.umlautRegEx[value], 'gi');
      output = output.replace(regEx, value);
    });
    // now replace all the other capital umlaute
    this.specialCharUpperCaseVariantArr.forEach(value => {
      const regEx = new RegExp(this.umlautUpperCaseRegEx[value], 'gi');
      output = output.replace(regEx, value);
    });
    return output;
  }

  replaceSpecialCharVariant(input: string): string {
    // replace all lower Umlauts variant to lower Umlauts
    let output = input;
    this.specialCharArr.forEach(value => {
      const regEx = new RegExp(this.umlautVarientRegEx[value], 'gi');
      output = output.replace(regEx, value);
    });
    // now replace all the other capital umlaute variant to capital umlaute
    this.specialCharUpperCaseArr.forEach(value => {
      const regEx = new RegExp(this.umlautVarientUpperCaseRegEx[value], 'gi');
      output = output.replace(regEx, value);
    });
    return output;
  }

  isStudyManagementOperationInProgress(states) {
    return states && states.extAppWorkItemState === 'Study Management';
  }

  isLocked(state) {
    return state && state.extAppWorkItemState === 'External_Read_Lock_Added' && !state.isAssigned;
  }

  /** Validates if the exam is being read */
  isReading(state) {
    return state && state.isActivated;
  }

  /** Validates if the exam is assigned to wfm Or Approver */
  wfmOrApproverAssignment(state) {
    return (
      state &&
      state.isAssigned &&
      (state.ownership.assignedToUser[0]['externalUser'] === false ||
        state.extAppWorkItemState === 'RA1000_Approver_Added')
    );
  }

  /** Validates if the exam is open for edit */
  wfmOrApproverEdit(state) {
    return state && state?.request?.actionCode === 'EDIT';
  }

  /** Validates if the exam is assigned externally */
  isExternallyOpened(state) {
    return (
      state &&
      state.isAssigned &&
      (state.extAppWorkItemState === 'External_Read_Lock_Added' ||
        state.extAppWorkItemState === 'RA1000_Approver_Added')
    );
    //&& ( vm.externalUserList !== false || vm.studyManegmentUserList !== false );
  }

  constructor(
    private windowRef: WindowRef,
    private translate: TranslateService,
    private examSharingPageService: ExamSharingPageService
  ) {
    this.applicationLaunchUrlParameters = window.location.search;
    this.proxyPath = environment.proxyPath;
  }

  getSharedTabContext() {
    return this.sharedTabContext;
  }

  setAssociationEnabled(flag) {
    this.associationFlag = flag;
  }

  isAssociationEnabled() {
    return this.associationFlag;
  }

  getViewerMessageQueue() {
    return this.viewerMessageQueue;
  }

  getUvEnabled() {
    return this.uvEnabled;
  }

  setUvEnabled(enabled) {
    this.uvEnabled = enabled;
  }

  isInvalidInput(searchParams: SearchParams) {
    if (
      searchParams.searchCriteriaType === SearchCriteriaType.ADVANCED_SEARCH &&
      !this.hasNameMRNOrAccesionSearchCriterion(searchParams)
    ) {
      for (const key in searchParams) {
        if (searchParams.skipProps.findIndex(prop => prop === key) < 0 && searchParams.hasOwnProperty(key)) {
          if (
            searchParams[key] !== undefined &&
            (typeof searchParams[key] === 'string' || typeof searchParams[key] === 'number')
          ) {
            //build up criteria for special characters check
            const criterion: any = {
              searchCriteriaType: key,
              searchCriteria: searchParams[key]
            };

            if (this.specialCharactersExist(criterion)) {
              return true;
            }
          }
        }
      }
    }
  }

  private hasNameMRNOrAccesionSearchCriterion(searchCriteriaInfo: SearchParams) {
    return (
      !!searchCriteriaInfo &&
      (searchCriteriaInfo.searchCriteriaType === SearchCriteriaType.PATIENT_NAME ||
        searchCriteriaInfo.searchCriteriaType === SearchCriteriaType.ACCESSION ||
        searchCriteriaInfo.searchCriteriaType === SearchCriteriaType.MRN ||
        searchCriteriaInfo.searchCriteriaType === SearchCriteriaType.NHI)
    );
  }

  specialCharactersExist(searchCriteriaInfo: any) {
    let specialChars = '<>@!#%&*()[]{}?:;|"\\/~`=';
    specialChars = this.needToReplaceSpecialChar(searchCriteriaInfo, specialChars);
    for (const specialChar of specialChars) {
      if (searchCriteriaInfo.searchCriteria && searchCriteriaInfo.searchCriteria.indexOf(specialChar) > -1) {
        log.error('Search criteria ' + searchCriteriaInfo.searchCriteria + ' has an invalid character ' + specialChar);
        return true;
      }
    }
    return false;
  }

  needToReplaceSpecialChar(searchCriteriaInfo, specialChars) {
    switch (searchCriteriaInfo.searchCriteriaType) {
      // for MRN case the searchCriteriaType is equal to 'MRN / Patient ID' when performing quick search
      // it is equal to 'MRN' when performing  Advanced search , both of this case should follow the same logic
      case 'mrn':
        specialChars = this.handlePrefixCase(searchCriteriaInfo.searchCriteria, specialChars);
        specialChars = this.handleSuffixCase(searchCriteriaInfo.searchCriteria, specialChars);
        break;

      case 'accession':
        specialChars = this.handlePrefixCase(searchCriteriaInfo.searchCriteria, specialChars);
        break;

      case 'dob':
        specialChars = specialChars.replace('/', '');
        break;

      case 'orderNumber':
        specialChars = specialChars.replace('%', '');
        break;
    }
    specialChars =
      searchCriteriaInfo.searchCriteriaType === 'patientName' ||
      searchCriteriaInfo.searchCriteriaType === 'descriptions'
        ? specialChars
        : specialChars + ',+ ';

    return specialChars;
  }

  handlePrefixCase(searchCriteria, specialChars) {
    const isPrefix = this.prefixRegex.test(searchCriteria);
    const lastIndex = searchCriteria.length - 1;
    const lastChar = searchCriteria.charAt(lastIndex);
    // the wildcard should exist only one time in the search criteria
    specialChars =
      isPrefix && searchCriteria.slice(0, lastIndex).indexOf(lastChar) < 0
        ? specialChars.replace(lastChar, '')
        : specialChars;
    return specialChars;
  }

  handleSuffixCase(searchCriteria, specialChars) {
    const isSuffix = this.suffixRegex.test(searchCriteria);
    const firstChar = searchCriteria.charAt(0);
    // the wildcard should exist only one time in the search criteria
    specialChars =
      isSuffix && searchCriteria.substr(1).indexOf(firstChar) < 0 ? specialChars.replace(firstChar, '') : specialChars;
    return specialChars;
  }

  getQueryParam(variableName) {
    return window[`appConfig`] && window[`appConfig`][variableName];
  }

  findQueryParamKey(variableName) {
    return !!(window[`appConfig`] && window[`appConfig`][variableName]);
  }

  urlParams() {
    const params = new URLSearchParams(this.applicationLaunchUrlParameters);
    if (params.has(appConstants.externalApp) || this.examSharingPageService.getInitialLaunchParameters()) {
      return '';
    } else {
      return this.applicationLaunchUrlParameters;
    }
  }

  isSharedExamUrl(url: string): boolean {
    const parsedUrl = new URL(url, window.location.origin);
    return parsedUrl.pathname === appConstants.appURLs.sharedExam;
  }

  getComparisons(id) {
    const comparisons = [];
    for (let i = 0; i < this.sharedTabContext.length; i++) {
      const tab = this.sharedTabContext[i];

      if (id === tab.id && tab.comparisons) {
        _.each(tab.comparisons.samePatient, sameComparison => {
          if (id !== sameComparison.id) {
            this.addComparison(sameComparison, comparisons);
          }
        });
        _.each(tab.comparisons.differentPatient, differentComparison => {
          this.addComparison(differentComparison, comparisons);
        });
        if (comparisons.length > 0) {
          const isDifferentPatient = tab.comparisons.differentPatient.length > 0;
          this.setDifferentPatientFlag(isDifferentPatient);
          return ',' + comparisons.join(',');
        }
      }
    }
    this.setDifferentPatientFlag(false);
    return '';
  }

  // formats the comparison item to be used in the comparison list.
  private addComparison(comparisonItem, comparisons: any[]) {
    comparisons.push('NCD:' + comparisonItem.id);
  }

  //Flag to denote if same patient or different patient comparison
  setDifferentPatientFlag(isDiffPat) {
    this.isDifferentPatientFlag = isDiffPat;
  }

  getDifferentPatientFlag() {
    return this.isDifferentPatientFlag;
  }

  getTabsLength() {
    return this.sharedTabContext.length;
  }

  getActiveTab() {
    for (const tab of this.sharedTabContext) {
      if (tab.active) {
        return tab;
      }
    }
  }

  getActiveTabs(tabs) {
    const result = [];
    for (const tab of tabs) {
      if (tab.active) {
        result.push(tab);
      }
    }
    return result;
  }

  getTab(id) {
    for (const tab of this.sharedTabContext) {
      if (tab.id === id) {
        return tab;
      }
    }
  }

  getTabWorkItem(id) {
    for (const tab of this.sharedTabContext) {
      if (tab.id === id) {
        return tab.workItem;
      }
    }
  }

  getExternalTab() {
    for (const tab of this.sharedTabContext) {
      if (tab.isExternalTab) {
        return tab;
      }
    }
  }

  getEmptyExternalTab() {
    for (const tab of this.sharedTabContext) {
      if (appConstants.externalTabId === tab.id && tab.isExternalTab) {
        return tab;
      }
    }
  }

  getTabContext(id) {
    for (const tab of this.sharedTabContext) {
      if (tab.id === id) {
        return tab.examContext;
      }
    }
  }

  isTabActive(id): boolean {
    for (const tab of this.sharedTabContext) {
      if (id === tab.id && tab.active) {
        return true;
      }
    }
    return false;
  }

  convertToWFMTabs() {
    for (const tab of this.sharedTabContext) {
      if (tab.id !== appConstants.externalTabId) {
        tab.isExternalTab = false;
      }
    }
  }

  getTabByAccession(acc) {
    for (const tab of this.sharedTabContext) {
      if (tab.workItem && tab.workItem.keys && tab.workItem.keys.procedureAccessionNo === acc) {
        return tab;
      }
    }
  }

  getActiveTabIndex() {
    for (let i = 0; i < this.sharedTabContext.length; i++) {
      if (this.sharedTabContext[i].active) {
        return i;
      }
    }
  }

  getTabIndex(id) {
    for (let i = 0; i < this.sharedTabContext.length; i++) {
      if (id === this.sharedTabContext[i].id) {
        return i;
      }
    }
    return -1;
  }

  getIndex(id) {
    for (let i = 0; i < this.sharedTabContext.length; i++) {
      if (id === this.sharedTabContext[i].id) {
        return i;
      }
    }
  }

  findIndex(list, property, value) {
    if (Array.isArray(list)) {
      for (let i = 0; i < list.length; i++) {
        if (list[i][property] === value) {
          return i;
        }
      }
    }
    return -1;
  }

  extractSessionInfo(data) {
    if (data) {
      const responseArray = [];
      let sessionId = '';
      let webSessionId = '';
      const dataArray = data.split('\n');
      _.each(dataArray, (res: string) => {
        const index = res.indexOf('=');
        if (index > -1) {
          const responseVal = [res.slice(0, index), res.slice(index + 1)];
          responseArray.push(responseVal);
        }
      });
      _.each(responseArray, response => {
        if (response.length === 2) {
          if (response[0] === 'SessionID') {
            sessionId = response[1];
          } else if (response[0] === 'WebSessionID') {
            webSessionId = response[1];
          }
        }
      });
      return { sessionId: sessionId.trim(), webSessionId: webSessionId.trim() };
    }
    return {};
  }

  private static getIEVersion() {
    const ua = window.navigator.userAgent;
    const msie = ua.indexOf('MSIE ');
    if (msie > 0) {
      // IE 10 or older
      return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
    }

    const trident = ua.indexOf('Trident/');
    if (trident > 0) {
      // IE 11
      const rv = ua.indexOf('rv:');
      return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
    }

    const edge = ua.indexOf('Edge/');
    if (edge > 0) {
      // Edge (IE 12+)
      return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
    }
    return 0;
  }

  private static isOtherNonIEBrowsersSupported() {
    const ua = window.navigator.userAgent;
    const uaSplit = ua.split(' ');
    let uaBrowser = '';
    if (uaSplit.length > 0) {
      uaBrowser = uaSplit[uaSplit.length - 1];
    }

    let browser = uaBrowser.indexOf('Edg');
    if (browser > -1 && uaBrowser.indexOf('Edge') < 0) {
      //Edge
      return true;
    }
    /* browser = uaBrowser.indexOf('Safari');
    if (browser > -1 && !window.navigator.brave) {
      //Chrome
      return true;
    } */
    // Need to find better way of detecting chrome
    browser = uaBrowser.indexOf('Firefox');
    if (browser > -1) {
      //Firefox
      return true;
    }
    return false;
  }

  isBrowserCompatible() {
    const browser = this.windowRef.browser;
    return browser.isChrome || browser.isChromiumBased || browser.isFirefox;
  }

  /* Validate supported Browsers
   *  Supported browsers are
   *  Google Chrome Version >= 84
   *  Microsoft Edge Version >= 84
   */
  isBrowserSupported() {
    try {
      const browserBrand = navigator['userAgentData'].brands[2].brand;
      const browserVersion = navigator['userAgentData'].brands[2].version;
      if (['Google Chrome', 'Microsoft Edge'].indexOf(browserBrand) >= 0 && browserVersion >= 84) {
        return true;
      }
    } catch (exception) {
      log.error('isBrowserSupported: Error parsing navigator property: ' + exception.description);
      return false;
    }
    return false;
  }

  setAppBaseUrl(url: string) {
    this.appBaseUrl = url;
  }

  getAppBaseUrl() {
    return this.appBaseUrl;
  }

  get filterCriteriaLimitTemplate() {
    return this._filterCriteriaLimitTemplate;
  }

  set filterCriteriaLimitTemplate(filterCriteriaLimitTemplate: TemplateRef<any>) {
    this._filterCriteriaLimitTemplate = filterCriteriaLimitTemplate;
  }

  get externalLoginApp() {
    return this.externalLoginApplication;
  }

  set externalLoginApp(externalApp) {
    this.externalLoginApplication = externalApp;
  }

  getExternalLoginApp() {
    return this.externalLoginApplication;
  }

  /**
   *  Function returns true only if NHI is valid
   */
  isValidNHI(nhi: string, pattern: string) {
    if (UtilService.isNull(nhi) || UtilService.isNull(pattern)) {
      return false;
    }

    const regex = new RegExp(pattern);
    return regex.test(nhi);
  }

  formatNHI(nhi: string, pattern: string, separator) {
    if (!UtilService.isNull(pattern) && this.isValidNHI(nhi, pattern)) {
      return nhi.match(pattern).slice(1).join(separator);
    }
    return nhi;
  }

  /**
   *  Function return the localized NHI-specific text resource based on the given NHI type.
   */
  getLocalizedNHISpecificText(translationKey: string, nhiType: string): string {
    if (translationKey && nhiType) {
      // try to get specialized string key (e.g. stringkey.ins)
      const specificTranslationKey = translationKey + '.' + nhiType.toLowerCase();
      let specificText = this.translate.instant(specificTranslationKey);
      if (specificText === specificTranslationKey) {
        // specific string does not exist, get the original key.
        specificText = this.translate.instant(translationKey, { NHI: nhiType.toUpperCase() });
      }
      return specificText;
    }
    return translationKey;
  }

  setSystemProp(data: any) {
    this.systemProp = data;
  }

  getSystemProp() {
    return this.systemProp;
  }

  static getLocalizedPriority(priority, translate: TranslateService) {
    if (
      priority &&
      (priority.toUpperCase() === appConstants.PRIORITIES.STAT ||
        priority.toUpperCase() === appConstants.PRIORITIES.ROUTINE)
    ) {
      priority = translate.instant('priority.' + priority);
    }
    return priority;
  }

  /**
   * Function: loopUntilSuccessOrMaxRetry
   *   Repeats callbackFunc until callbackFunc returns true or maxRetry are over
   * @param callbackFunc function to be called repeatedly. should return run pass/fail boolean value
   * @param id identifier for debug logs
   * @param maxRetry OPTIONAL - number of retries. default is set to THREE 'appConstants.COUNT.THREE'
   * @param delay OPTIONAL - time interval between attempts. default is set to 500ms 'appConstants.TIME.MS_FIVE_HUNDRED'
   */
  public loopUntilSuccessOrMaxRetry = (
    callbackFunc: (lastAttempt: boolean) => boolean,
    id,
    maxRetry = appConstants.COUNT.THREE,
    timeout = appConstants.TIME.MS_FIVE_HUNDRED,
    attempt = 1
  ): void => {
    const isSuccess = callbackFunc(attempt >= maxRetry);
    if (!(isSuccess || attempt >= maxRetry)) {
      log.debug(`loopUntilSuccessOrMaxRetry - id:'${id}' - attempt: ${attempt}/${maxRetry}`);
      setTimeout(this.loopUntilSuccessOrMaxRetry, timeout, callbackFunc, id, maxRetry, timeout, ++attempt);
    } else {
      log.debug(`loopUntilSuccessOrMaxRetry - id:'${id}' - loop completed`);
    }
  };

  /**
   * Validates the provided date-time formats.
   *
   * This method checks if the given date-time formats are valid
   * by using the `moment` library. if the parsed date is valid.
   * @param formats - A list of date-time format strings to validate.
   * @returns {boolean} - Returns `true` if all formats are valid, otherwise `false`.
   */
  static validateDateFormats(...formats: string[]): boolean {
    let valid = true;
    if (!formats || formats.length === 0) {
      return false;
    }
    const momentJs = moment;
    formats.forEach(format => {
      const testDate = momentJs().format(format);
      const isValid = momentJs(testDate, format, true).isValid();
      if (!isValid) {
        valid = false;
      }
    });
    return valid;
  }

  /**
   * Convert the string message to JSON.

   * @param message - A string message that needs to be parsed.
   * @returns {any} - Returns `Object` if message is valid JSON, otherwise `empty`.
   */
  static deserializeString(message: string): any {
    const messages = message.substring(message.indexOf(appConstants.messageDelimiter) + 1);
    if (messages) {
      try {
        return JSON.parse(messages);
      } catch (e) {
        log.debug(`Real time update message parsing Error, MESSAGE - [${message}] ERROR - ${JSON.stringify(e)}`);
        return message;
      }
    }
    return '';
  }

  /**
   * Retries a promise-returning function multiple times with delay on failure.
   * @param requestPromise - A function that returns a Promise
   * @param retryMaxcount - Maximum number of retry attempts before failing permanently
   * @param retryDelay - Delay in milliseconds between retry attempts
   * @param retryMessage - Message prefix to use in debug logs for identifying this retry operation
   * @returns {Promise<any>} A promise that resolves with the result of the successful attempt,
   *                         or rejects after exhausting all retry attempts
   * @example
   * // Retry an HTTP request up to 3 times with 1 second delay
   * UtilService.retryRequest(
   *   () => httpClient.get('/api/data'),
   *   3,
   *   1000,
   *   'Retrying HTTP Promise Call'
   * ).then(data => console.log(data))
   *  .catch(err => console.error('All retries failed', err));
   */
  public static retryRequest(
    requestPromise: () => Promise<any>,
    retryMaxcount: number,
    retryDelay: number,
    retryMessage: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const request$ = defer(requestPromise);
      const subscription = request$
        .pipe(
          retryWhen(errors =>
            errors.pipe(
              scan((retryCount, error) => {
                log.debug(`${retryMessage}: Retrying ${retryCount + 1}/${retryMaxcount}`);

                if (retryCount < retryMaxcount - 1) {
                  return retryCount + 1;
                }
                throw error;
              }, 0),
              catchError(error => {
                log.warn('Max retries reached', error);
                throw error;
              }),
              delay(retryDelay)
            )
          )
        )
        .subscribe(
          data => resolve(data),
          error => reject(error),
          () => {
            if (subscription && !subscription.closed) {
              subscription.unsubscribe();
            }
          }
        );
    });
  }

  /**
   * Generates a universally unique identifier (UUID) v4.
   * @returns {string} A randomly generated UUID v4 string (e.g., '9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d')
   * @example
   * const uniqueId = UtilService.getUUID();
   */
  public static getUUID(): string {
    return uuidV4();
  }

  /**
   * Masks patient identifiable information for logging purposes
   * Keeps first character and masks the rest with asterisks
   * @param resource The FhirCastResource to mask
   * @returns A sanitized copy of the resource safe for logging
   */
  public static maskFhircastResource(resource: FhirCastResource): any {
    if (!resource) {
      return null;
    }

    const maskStr = (value: string) => {
      return value.replace(/^(.)(.*)$/, (_, f, r) => f + '*'.repeat(r.length) + (r.length > 10 ? '...' : ''));
    };
    const maskedResource: FhirCastResource = JSON.parse(JSON.stringify(resource));
    if (maskedResource?.event?.['hub.event'] && maskedResource.event?.context?.length) {
      maskedResource.event.context = maskedResource.event.context.map(context => {
        if (context?.key === 'patient') {
          context.resource = { ...context?.resource } as Patient;
          if (context?.resource?.name?.length) context.resource.name = [{ text: '**', family: '**', given: ['***'] }];
          if (context?.resource?.birthDate)
            context.resource.birthDate = maskStr((context?.resource as Patient).birthDate);
          if (context?.resource?.gender)
            context.resource.gender = maskStr((context?.resource as Patient).gender) as any;
        }

        if (context.key === 'study') {
          context.resource = { ...context?.resource } as ImagingStudy;
          if (context?.resource?.identifier) {
            context.resource.identifier = [{ system: 'urn:dicom:uid', value: 'urn:oid:' + '****.******.******' }];
          }
        }
        return context;
      });
    }
    return maskedResource;
  }

  /**
   * Executes a callback function after a specified delay
   * @param delay The time to wait before executing the callback, in milliseconds
   * @param callbackFunc The function to execute after the delay
   * @returns A Promise that resolves to true when callback executes successfully, or rejects with false on error
   */
  public static executeWithDelay(delay: number, callbackFunc: () => void): Promise<boolean> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          callbackFunc();
          resolve(true);
        } catch (error) {
          log.error(`executeWithDelay: Error occurred executig callback: `, callbackFunc, ' Error: ', error);
          reject(false);
        }
      }, delay);
    });
  }

  /**
   * Changes the cursor state and manages a transparent overlay to block user interactions.
   *
   * @param state - The cursor state to set ('progress' or 'default')
   *                'progress': Shows progress cursor and blocks UI interactions
   *                'default': Restores normal cursor and UI interactions
   */
  public static changeCursor(state: 'progress' | 'default') {
    let overlayElement = document.getElementById('cursor-overlay');
    const isProgress = state === 'progress';

    // Create the overlay element if it doesn't exist
    if (isProgress && !overlayElement) {
      overlayElement = document.createElement('div');
      overlayElement.id = 'cursor-overlay';
      overlayElement.style.cssText =
        'position:absolute;top:0;left:0;width:100%;height:100%;z-index:9999;background-color:transparent;cursor:progress;';
      document.body.appendChild(overlayElement);
    }

    // Update visibility if element exists
    if (overlayElement) {
      overlayElement.style.display = isProgress ? 'block' : 'none';
    }
  }

  /**
   * Creates a URL by combining origin, path, and query parameters
   *
   * @param origin - The base URL or origin (e.g., 'https://example.com')
   * @param path - A string or array of path segments to append to the origin
   * @param queryParams - An object containing query parameters to add to the URL
   * @returns A fully formatted URL string or null if error
   */
  public static createUrl(origin: string, path: string[] | string, queryParams: Record<string, string> = {}): string {
    try {
      // Extract clean origin using URL API
      origin = new URL(origin).origin;

      // Process path based on type
      let pathString: string = Array.isArray(path) ? path.join('/') : path;

      // Ensure path starts with '/' if not empty
      if (pathString && !pathString.startsWith('/')) {
        pathString = '/' + pathString;
      }

      let url = origin + (pathString || '');

      // Add query parameters if present
      if (queryParams && Object.keys(queryParams).length > 0) {
        const queryString = Object.keys(queryParams)
          .map(key => `${key}=${queryParams[key]}`)
          .join('&');
        if (queryString) {
          url += '?' + queryString;
        }
      }

      return url;
    } catch (error) {
      log.error('createUrl : Error: ', error);
      return null;
    }
  }
}
